import React, { useState, useEffect, useMemo, useRef } from "react";
import { Polygon, InfoWindow, Circle } from "@react-google-maps/api";
import { Button, Select, Spin } from "antd";
import TabOne from "./TabOne/TabOne"
import { Filter } from "lucide-react";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import { useQuery } from "react-query";
import TabTwo from "./TabTwo/TabTwo";
import { useTranslation } from "react-i18next";

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

const SurveyAnalyticsMap = ({ mapRef, polygonPaths,surveyId }) => {
  const { t } = useTranslation();
  const userString = localStorage.getItem("user");
  const user = JSON.parse(userString);
  const axiosInstance=useAxiosInstance();
  const initialBoundsRef = useRef(null);
  const initialZoomRef = useRef(null);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [tabs, setTabs] = useState({
  tab1: "Tab-1",
  tab2: "Tab-2",
});
  const [activeTab, setActiveTab] = useState("tab1");
  const [legendColors, setLegendColors] = useState([]);
  const [surveyData,setSurveyData] = useState();
   const [filters, setFilters] = useState({
    constituency: "",
    village: "",
    booth: "",
  });
  const [favourabilityColors, setFavourabilityColors] = useState([]);
  useEffect(() => {
  if (
    !polygonPaths ||
    !polygonPaths.length ||
    !mapRef?.current ||
    initialBoundsRef.current
  ) {
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();

  polygonPaths.forEach((poly) => {
    poly.coordinate?.forEach((point) => bounds.extend(point));
  });

  mapRef.current.fitBounds(bounds);

  // Zoom slightly more & STORE final zoom
  window.google.maps.event.addListenerOnce(
    mapRef.current,
    "bounds_changed",
    () => {
      const zoom = mapRef.current.getZoom() + 0.5; 
      mapRef.current.setZoom(zoom);

      // Save initial state
      initialBoundsRef.current = bounds;
      initialZoomRef.current = zoom;
    }
  );
}, [polygonPaths, mapRef]);



  /* -------------------- FETCH SURVEY TABS -------------------- */
  const { data: tabResponse } = useQuery(
    ["survey-tabs", surveyId],
    async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/surveyTabs/${surveyId}`
      );
      return res?.data;
    },
    {
      enabled: !!surveyId,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  useEffect(() => {
    if (!tabResponse?.status || !tabResponse?.data) return;

    setTabs({
      tab1: tabResponse.data.tab1?.label || "Tab-1",
      tab2: tabResponse.data.tab2?.label || "Tab-2",
    });
  }, [tabResponse]);

  /* -------------------- FETCH SURVEY RESPONSE -------------------- */

  useEffect(() => {
    if (!surveyId) return;

    const fetchSurvey = async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/surveys/${surveyId}`
      );
      setSurveyData(res?.data?.data || null);
    };

    fetchSurvey();
  }, [surveyId]);

  const surveyType = surveyData?.surveyType;
    
  console.log("filters",filters)

  /* -------------------- RESPONDENTS -------------------- */
  const {
    data: respondentsDataset = [],
    isLoading: isRespondentsLoading,
  } = useQuery(
    [
      "survey-respondents",
      surveyId,
      surveyType,
      filters.constituency,
      filters.village,
      filters.booth,
    ],
    async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/allRespondants/${surveyId}`,
        {
          params: {
            constituency:
              surveyType === "parliamentary"
                ? filters.constituency || undefined
                : undefined,
            village: filters.village || undefined,
            booth: filters.booth || undefined
          },
        }
      );
      return res?.data?.data || [];
    },
    {
      enabled: !!surveyId && !!surveyType,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  /* -------------------- OPTIONS FROM API -------------------- */
  const constituencyOptions =
    (surveyType === "parliamentary" || surveyType==="state")
      ? uniq(respondentsDataset.map((d) => d.constituency || d.pcName))
      : [];
  const villageOptions =
    (surveyType === "parliamentary" || surveyType==="state")
      ? filters.constituency
        ? uniq(respondentsDataset.map((d) => d.village))
        : []
      : uniq(respondentsDataset.map((d) => d.village));

  const boothOptions = filters.village
    ? uniq(respondentsDataset.map((d) => d.booth))
    : [];

  const fetchLegendColors = async () => {
    try {
      const res = await axiosInstance.get(`/api/surveyAnalytics/surveyColor/${surveyId}`);
      const data = res?.data?.data || [];

      setLegendColors(data);
    } catch (err) {
      console.error("Failed to load legend colors:", err);
    }
  };

  useEffect(() => {
    if (!surveyId) return;
    fetchLegendColors();
  }, [surveyId]);

  const getLegendColor = (polygonName) => {
    if (!polygonName) return null;

    const nameUpper = polygonName.toString().trim().toUpperCase();

    const match = legendColors.find(
      (entry) => entry.location?.toString().trim().toUpperCase() === nameUpper
    );

    return match ? match.color : null;
  };

  const selectPolygonByName = (name, level, shouldSelectPolygon = true) => {
  
    if (!name || !polygonPaths?.length) return;

  const polygon = polygonPaths.find(
    (p) => p.name?.toUpperCase() === name.toUpperCase()
  );

  if (!polygon && shouldSelectPolygon) return;

  // Always update filters
  setFilters((prev) => ({
    ...prev,
    ...(level === "constituency"
      ? { constituency: name, village: "", booth: "" }
      : { village: name, booth: "" }),
  }));

  // Only select polygon + zoom if allowed
  if (!shouldSelectPolygon) return;

  setSelectedPolygon(polygon);

  const bounds = new window.google.maps.LatLngBounds();
  polygon.coordinate.forEach((p) => bounds.extend(p));
  mapRef.current?.fitBounds(bounds);
};

  /* -------------------- MAP HANDLERS -------------------- */
  const handlePolygonClick = (polygon) => {
    setSelectedPolygon(polygon);

    setFilters((prev) => ({
      ...prev,
      ...((surveyType === "parliamentary" || surveyType==="state")
        ? { constituency: polygon.name, village: "", booth: "" }
        : { village: polygon.name, booth: "" }),
    }));

    const bounds = new window.google.maps.LatLngBounds();
    polygon.coordinate.forEach((p) => bounds.extend(p));
    mapRef.current?.fitBounds(bounds);
  };

  const handleBackClick = () => {
    setSelectedPolygon(null);
    setFilters({ constituency: "", village: "", booth: "" });

    if (!mapRef.current) return;

    if (initialBoundsRef.current && initialZoomRef.current !== null) {
      mapRef.current.fitBounds(initialBoundsRef.current);
      mapRef.current.setZoom(initialZoomRef.current);
    }
  };
  const renderPolygons = () => {
    if (!polygonPaths || !Array.isArray(polygonPaths)) return null;
    return polygonPaths.map((polygon, index) => {
      const bounds = new window.google.maps.LatLngBounds();
      polygon?.coordinate?.forEach((point) => bounds.extend(point));
      const isSelected = selectedPolygon?.name === polygon.name;
      const isInfluencerMode = activeTab === "influencer";

      const favourabilityMatch =
        activeTab === "tab2"
          ? favourabilityColors.find((f) => {
              if (surveyType === "parliamentary" || surveyType === "state") {
                return (
                  f.constituency?.toUpperCase() ===
                  polygon.name?.toUpperCase()
                );
              }

              return (
                f.village?.toUpperCase() ===
                polygon.name?.toUpperCase()
              );
            })
          : null;

      const legendColor =
        activeTab === "tab2"
          ? favourabilityMatch?.color
          : getLegendColor(polygon.name);

      return (
        <React.Fragment key={index}>
          <Polygon
            paths={polygon.coordinate}
            onClick={() => handlePolygonClick(polygon)}
            options={{
              strokeColor: "#0D47A1",
              strokeOpacity: isInfluencerMode ? 1 : 0.9,
              strokeWeight: 2,
              fillColor: legendColor || "transparent",
              fillOpacity: isInfluencerMode ? 0.55 : isSelected ? 1 : 0.8,
              zIndex: isSelected ? 1000 : 999,
            }}
          />

      {isSelected && (
        <InfoWindow
          position={bounds.getCenter()}
          onCloseClick={handleBackClick}
        >
          <div className="bg-white/90 backdrop-blur-md border border-gray-200 p-3 rounded-lg min-w-[220px]">
            
            {/* COMMON */}
            <div className="text-lg font-bold text-[#123865] mb-2">
              {selectedPolygon.name}
            </div>

            {/* TAB-2: FAVOURABILITY VIEW */}
            {activeTab === "tab2" && favourabilityMatch && (
              <>
                <div className="text-sm text-gray-700 mb-1">
                  <strong>Party:</strong> {favourabilityMatch.party}
                </div>

                <div className="flex items-center justify-center gap-2 mt-1">
                  <span
                    className="px-2 py-1 rounded text-white text-xs font-semibold text-center"
                    style={{ backgroundColor: favourabilityMatch.color }}
                  >
                    {favourabilityMatch.category}
                  </span>
                </div>
              </>
            )}

            <div className="mt-3 flex justify-center">
              <Button onClick={handleBackClick} size="small">
                Back
              </Button>
            </div>
          </div>
        </InfoWindow>
      )}
      </React.Fragment>
      );
    });
  };

  if (isRespondentsLoading)
    return (
      <div className="flex justify-center items-center h-full">
        <Spin size="large" />
      </div>
    );

  return (
    <>
      {renderPolygons()}

      {/* Filters */}
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-lg border border-gray-200 shadow-lg rounded-xl p-3 z-[9999]">
        <div className="flex flex-col gap-3 items-center">
          <div className="flex items-center gap-2 mb-1">
            <Filter size={18} className="text-blue-600" />
            <span className="font-semibold text-gray-700">{t("filters")}</span>
          </div>

          {(surveyType === "parliamentary" || surveyType==='state') && (
            <Select
              placeholder= {t("selectConstituency")}
              value={filters.constituency || undefined}
              onChange={(val) => {
                if (!val) {
                  setSelectedPolygon(null);
                  setFilters({
                    constituency: "",
                    village: "",
                    booth: "",
                  });

                  const bounds = new window.google.maps.LatLngBounds();
                  polygonPaths.forEach((poly) =>
                    poly.coordinate.forEach((p) => bounds.extend(p))
                  );
                  mapRef.current?.fitBounds(bounds);

                  return;
                }
                selectPolygonByName(val, "constituency");
              }}
              options={constituencyOptions.map((c) => ({
                value: c,
                label: c,
              }))}
              getPopupContainer={(trigger) => trigger.parentNode}
              loading={isRespondentsLoading}
              allowClear
              showSearch
              style={{ width: 220 }}
            />
          )}

          {surveyType!=="state" && 
            <Select
              placeholder={user?.urbanRural==='Rural'? "Select Village" : "Select Ward"}
              value={filters.village || undefined}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(val) => {
                if (!val) {
                  setSelectedPolygon(null);
                  setFilters((prev) => ({ ...prev, village: "", booth: "" }));

                  const bounds = new window.google.maps.LatLngBounds();
                  polygonPaths.forEach((poly) =>
                    poly.coordinate.forEach((p) => bounds.extend(p))
                  );
                  mapRef.current?.fitBounds(bounds);

                  return;
                }

                if (surveyType === "assembly") {
                  // Select polygon + field
                  selectPolygonByName(val, "village", true);
                } else if (surveyType === "parliamentary") {
                  // Only set field, DO NOT select polygon
                  selectPolygonByName(val, "village", false);
                } else {
                  // Default behavior
                  selectPolygonByName(val, "village", true);
                }
              }}
              disabled={
                (surveyType === "parliamentary" || surveyType==="state") && !filters.constituency
              }
              options={villageOptions.map((v) => ({
                value: v,
                label: v,
              }))}
              loading={isRespondentsLoading}
              allowClear
              showSearch
              style={{ width: 220 }}
            />
          }

          {/* BOOTH */}
          {surveyType!=="state" && 
            <Select
              placeholder="Select Booth"
              value={filters.booth || undefined}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, booth: val || "" }))
              }
              disabled={!filters.village}
              options={boothOptions.map((b) => ({
                value: b,
                label: b,
              }))}
              loading={isRespondentsLoading}
              allowClear
              showSearch
              style={{ width: 220 }}
            />
          }
        </div>
      </div>

      {/* Top Tabs */}
      <div className="p-4 top-4 absolute flex gap-3 border-b pb-2">
        <button
          className={`px-4 py-2 rounded-md font-medium transition-all ${
            activeTab === "tab1"
              ? "bg-[#004877] text-white"
              : "bg-white/10 backdrop-blur-[70px] border border-white/30 rounded-3xl p-4 shadow-[0_6px_24px_0_rgba(31,38,135,0.25)] text-gray-700 hover:bg-gray-200"
          }`}
          onClick={() => setActiveTab("tab1")}
        >
          {tabs?.tab1}
        </button>

        <button
          className={`px-4 py-2 rounded-md font-medium transition-all ${
            activeTab === "tab2"
              ? "bg-[#004877] text-white"
              : "bg-white/10 backdrop-blur-[70px] border border-white/30 rounded-3xl p-4 shadow-[0_6px_24px_0_rgba(31,38,135,0.25)] text-gray-700 hover:bg-gray-200"
          }`}
          onClick={() => setActiveTab("tab2")}
        >
          {tabs?.tab2}
        </button>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === "tab1" && (
          <TabOne
            survey={surveyData}
            filters={filters}
          />
        )}

        {activeTab === "tab2" && (
          <TabTwo
            survey={surveyData}
            filters={filters}
            setFavourabilityColors={setFavourabilityColors}
          />
        )}

      </div>
    </>
  );
};

export default SurveyAnalyticsMap;
